import { mapSliderProductApiData } from './Products';
import { theme } from 'components/Theme/main';
import { mapAvailabilityData, mapStoreAvailability } from 'connectors/availability/Availability';
import { getFirstImage } from 'connectors/image/Image';
import { mapLoyaltyPointsObtainedData, mapProductPriceData, mapProductPricesData } from 'connectors/price/Prices';
import {
    MainVariantDetailFragmentApi,
    ProductColorVariantFragmentApi,
    ProductDetailFragmentApi,
    ProductDetailInterfaceFragmentApi,
    VariantDetailFragmentApi,
} from 'graphql/generated';
import { ProductFileType } from 'types/file';
import {
    ColorVariantType,
    PictogramParameterType,
    ProductDetailInterfaceType,
    ProductDetailType,
    VariantDetailType,
} from 'types/product';
import { ReviewsSummary, ReviewType } from 'types/review';

const mapProductDetailInterface = (
    productDetailApiData: ProductDetailInterfaceFragmentApi,
    currencyCode: string,
): ProductDetailInterfaceType => {
    return {
        ...productDetailApiData,
        __typename: productDetailApiData.__typename,
        description: productDetailApiData.description !== null ? productDetailApiData.description : '',
        price: mapProductPriceData(productDetailApiData.price, currencyCode),
        prices: mapProductPricesData(productDetailApiData.prices, currencyCode),
        loyaltyClubPointsObtained: mapLoyaltyPointsObtainedData(productDetailApiData.loyaltyClubPointsObtained),
        accessories: mapSliderProductApiData(productDetailApiData.accessories, currencyCode),
        relatedProducts: mapSliderProductApiData(productDetailApiData.relatedProducts, currencyCode),
        spareParts: mapSliderProductApiData(productDetailApiData.spareParts, currencyCode),
        images: productDetailApiData.images.filter((image) => image !== productDetailApiData.flexiSpaceImage[0]),
        availability: mapAvailabilityData(productDetailApiData.availability),
        deliveryDaysFromCentralStore: productDetailApiData.deliveryDaysFromCentralStore,
        youtubeVideoId: productDetailApiData.youtubeVideoId,
        benefits: productDetailApiData.benefits !== null ? productDetailApiData.benefits : [],
        brand: productDetailApiData.brand,
        parameterImage: getFirstImage(productDetailApiData.parameterImage),
        pictogramParameters: mapPictogramParameters(productDetailApiData),
        isSellingDenied: productDetailApiData.isSellingDenied,
        files: mapProductFilesTypes(productDetailApiData.files),
        defaultStoreAvailability:
            productDetailApiData.defaultStoreAvailability !== null
                ? mapStoreAvailability(productDetailApiData.defaultStoreAvailability)
                : null,
        categoryNames: productDetailApiData.categories.map((category) => category.name),
        isElectronicGiftVoucher: productDetailApiData.isElectronicGiftVoucher,
        alternateUrls: productDetailApiData.alternateUrls ?? [],
        centralStockQuantity: productDetailApiData.centralStockQuantity,
        isVoucher: productDetailApiData.isVoucher,
        referencePrice:
            productDetailApiData.referencePrice !== null
                ? Number.parseFloat(productDetailApiData.referencePrice)
                : null,
        reviews: productDetailApiData.reviews as ReviewType[],
        reviewsSummary: productDetailApiData.reviewsSummary as ReviewsSummary,
        pricePerUnit: productDetailApiData.pricePerUnit
            ? {
                  unit: productDetailApiData.pricePerUnit.unit,
                  price: Number.parseFloat(productDetailApiData.pricePerUnit.price),
              }
            : null,
    };
};

export const mapProductDetailApiData = (
    productDetailApiData: ProductDetailFragmentApi,
    currencyCode: string,
): ProductDetailType => {
    return {
        ...mapProductDetailInterface(productDetailApiData, currencyCode),
        __typename: productDetailApiData.__typename,
    };
};

export const mapVariantDetailApiData = (
    apiData: MainVariantDetailFragmentApi | VariantDetailFragmentApi,
    currencyCode: string,
): VariantDetailType => {
    return {
        ...mapProductDetailInterface(apiData, currencyCode),
        __typename: apiData.__typename,
        colorVariants: apiData.colorVariants.map((variant) => mapColorVariant(variant)),
        currentColorVariant: 'currentColorVariant' in apiData ? mapColorVariant(apiData.currentColorVariant) : null,
    };
};

const mapColorVariant = (variant: ProductColorVariantFragmentApi): ColorVariantType => ({
    ...variant,
    value: variant.value ?? theme.color.gray,
    colorImage: getFirstImage(variant.parameterValueImages),
    image: getFirstImage(variant.images),
});

export const mapPictogramParameters = (apiData: ProductDetailInterfaceFragmentApi): PictogramParameterType[] | null => {
    const mappedData = [];

    for (const parameter of apiData.pictogramParameters) {
        if (parameter !== null && parameter.isPictogramType) {
            mappedData.push({
                uuid: parameter.uuid,
                name: parameter.name,
                image: getFirstImage(parameter.images),
                slug: parameter.slug,
            });
        }
    }

    if (mappedData.length !== 0) {
        return mappedData;
    }

    return null;
};

export const mapProductFilesTypes = (apiData: ProductDetailInterfaceFragmentApi['files']): ProductFileType[] => {
    const mappedData = [];

    for (const file of apiData) {
        mappedData.push({
            ...file,
            type: file.type as ProductFileType['type'],
        });
    }

    return mappedData;
};
